// A thin, SAFE GA4 wrapper. NEVER throws; every call no-ops unless GA is BOTH configured
// (VITE_FB_MEASUREMENT_ID present) and supported (await isSupported() in this environment).
// Init is lazy + memoized behind ONE promise; on init we declare Consent Mode defaults
// (all ad_* surfaces denied) so GA never spins up Signals / ads personalization. Drop the
// env var → GA silently no-ops, leaving the in-app /admin console as the sole telemetry surface.
import { getAnalytics, isSupported, logEvent, setConsent, type Analytics } from "firebase/analytics";
import { app } from "./firebase";

export type AnalyticsEvent =
  | "sign_in"
  | "generate"
  | "generate_failed"
  | "export_video"
  | "export_cancelled"
  | "share_link";

let _initPromise: Promise<Analytics | null> | null = null;

/** Resolve the Analytics instance ONCE — null unless measurementId is set AND isSupported()
 *  is true. Declares Consent Mode defaults just before creating the instance. */
function init(): Promise<Analytics | null> {
  if (_initPromise) return _initPromise;
  _initPromise = (async () => {
    const measurementId = import.meta.env.VITE_FB_MEASUREMENT_ID;
    if (!measurementId) return null;
    try {
      if (!(await isSupported())) return null;
      // Consent Mode: analytics granted for our first-party allowlisted context; every ad
      // surface denied so GA never engages Google Signals / ads personalization.
      setConsent({
        analytics_storage: "granted",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      });
      return getAnalytics(app);
    } catch {
      return null;
    }
  })();
  return _initPromise;
}

/** Fire-and-forget: awaits init, logs when GA is live, swallows ALL errors. */
export function track(
  event: AnalyticsEvent,
  params?: Record<string, string | number | boolean>,
): void {
  void init()
    .then((analytics) => {
      if (analytics) logEvent(analytics, event, params);
    })
    .catch(() => {});
}
